import { hoyLocal, fechaLocal } from '@/lib/fechas';
import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Calculator, DollarSign, Plus, Trash2, Users, Receipt, ArrowUpRight, ArrowDownRight, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIAS = [
  { valor: 'refacciones', label: 'Refacciones' },
  { valor: 'renta', label: 'Renta' },
  { valor: 'servicios', label: 'Servicios (luz, agua, etc.)' },
  { valor: 'herramientas', label: 'Herramientas' },
  { valor: 'nomina', label: 'Nomina' },
  { valor: 'impuestos', label: 'Impuestos' },
  { valor: 'otros', label: 'Otros' },
];

function getWeekRange() {
  const hoy = new Date();
  const lunes = new Date(hoy);
  lunes.setDate(hoy.getDate() - hoy.getDay() + (hoy.getDay() === 0 ? -6 : 1));
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  return { desde: fechaLocal(lunes), hasta: fechaLocal(domingo) };
}

function getMonthRange() {
  const hoy = new Date();
  const inicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const fin = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  return { desde: fechaLocal(inicio), hasta: fechaLocal(fin) };
}

export default function Contabilidad() {
  const location = useLocation();
  const [tab, setTab] = useState(location.state?.tab || 'resumen');
  const [rango, setRango] = useState(getWeekRange);
  const [resumen, setResumen] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [ingresos, setIngresos] = useState([]);
  const [nomina, setNomina] = useState([]);
  const [staff, setStaff] = useState([]);
  const [cargando, setCargando] = useState(true);

  const [gastoDialog, setGastoDialog] = useState(false);
  const [gastoForm, setGastoForm] = useState({ fecha: '', categoria: 'otros', descripcion: '', monto: '' });
  const [guardandoGasto, setGuardandoGasto] = useState(false);

  const [nominaDialog, setNominaDialog] = useState(false);
  const [nominaForm, setNominaForm] = useState({ empleado_id: '', empleado_nombre: '', fecha: '', periodo: '', monto: '', notas: '' });
  const [guardandoNomina, setGuardandoNomina] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde: rango.desde, hasta: rango.hasta };
      const [resRes, gasRes, ingRes, nomRes, staffRes] = await Promise.all([
        api.get('/contabilidad/resumen', { params }),
        api.get('/contabilidad/gastos', { params }),
        api.get('/contabilidad/ingresos', { params }),
        api.get('/contabilidad/nomina', { params }).catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
      ]);
      setResumen(resRes.data);
      setGastos(gasRes.data);
      setIngresos(ingRes.data);
      setNomina(nomRes.data);
      setStaff(staffRes.data);
    } catch { toast.error('Error al cargar contabilidad'); }
    finally { setCargando(false); }
  }, [rango]);

  useEffect(() => { cargar(); }, [cargar]);

  const crearGasto = async () => {
    if (!gastoForm.monto || !gastoForm.descripcion) { toast.error('Monto y descripción obligatorios'); return; }
    setGuardandoGasto(true);
    try {
      await api.post('/contabilidad/gastos', gastoForm);
      toast.success('Gasto registrado');
      setGastoDialog(false);
      cargar();
    } catch { toast.error('Error al registrar gasto'); }
    finally { setGuardandoGasto(false); }
  };

  const eliminarGasto = async (id) => {
    try { await api.delete(`/contabilidad/gastos/${id}`); toast.success('Gasto eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const crearNomina = async () => {
    if (!nominaForm.empleado_id || !nominaForm.monto) { toast.error('Empleado y monto obligatorios'); return; }
    setGuardandoNomina(true);
    try {
      await api.post('/contabilidad/nomina', nominaForm);
      toast.success('Pago de nomina registrado');
      setNominaDialog(false);
      cargar();
    } catch { toast.error('Error al registrar pago'); }
    finally { setGuardandoNomina(false); }
  };

  const eliminarNomina = async (id) => {
    try { await api.delete(`/contabilidad/nomina/${id}`); toast.success('Pago eliminado'); cargar(); }
    catch { toast.error('Error al eliminar'); }
  };

  const tabs = [
    { id: 'resumen', label: 'Corte de Caja', icono: Receipt },
    { id: 'ingresos', label: 'Ingresos', icono: ArrowUpRight },
    { id: 'gastos', label: 'Gastos', icono: ArrowDownRight },
    { id: 'nomina', label: 'Nomina', icono: Users },
  ];

  if (cargando && !resumen) return <div className="space-y-4"><Skeleton className="h-10 w-64 bg-slate-800" /><Skeleton className="h-64 bg-slate-800" /></div>;

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="contabilidad-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="font-barlow text-3xl font-bold uppercase tracking-tight text-slate-50 flex items-center gap-3">
          <Calculator className="w-8 h-8 text-amber-500" /> Contabilidad
        </h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setRango(getWeekRange())}
            className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs" data-testid="rango-semana-btn">Esta Semana</Button>
          <Button variant="outline" size="sm" onClick={() => setRango(getMonthRange())}
            className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs" data-testid="rango-mes-btn">Este Mes</Button>
          <Input type="date" value={rango.desde} onChange={e => setRango({...rango, desde: e.target.value})}
            className="bg-slate-900 border-slate-700 text-slate-200 w-36 text-xs" data-testid="rango-desde-input" />
          <span className="text-slate-500 text-xs">a</span>
          <Input type="date" value={rango.hasta} onChange={e => setRango({...rango, hasta: e.target.value})}
            className="bg-slate-900 border-slate-700 text-slate-200 w-36 text-xs" data-testid="rango-hasta-input" />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-900/60 p-1 rounded-sm w-fit flex-wrap">
        {tabs.map(t => {
          const Icon = t.icono;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${tab === t.id ? 'bg-amber-500 text-black' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid={`tab-${t.id}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* TAB: Resumen */}
      {tab === 'resumen' && resumen && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-slate-900/40 border-slate-800 cursor-pointer hover:border-green-800 transition-colors" onClick={() => setTab('ingresos')} data-testid="metric-ingresos">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Ingresos</p>
                    <p className="text-2xl font-bold text-green-400 font-barlow">{formatoMXN(resumen.ingresos)}</p>
                    <p className="text-[10px] text-slate-600 mt-1">Clic para ver desglose</p>
                  </div>
                  <div className="w-10 h-10 rounded-sm bg-green-500/10 flex items-center justify-center">
                    <ArrowUpRight className="w-5 h-5 text-green-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 border-slate-800 cursor-pointer hover:border-red-800 transition-colors" onClick={() => setTab('gastos')} data-testid="metric-gastos">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Gastos</p>
                    <p className="text-2xl font-bold text-red-400 font-barlow">{formatoMXN(resumen.gastos)}</p>
                    <p className="text-[10px] text-slate-600 mt-1">Clic para ver desglose</p>
                  </div>
                  <div className="w-10 h-10 rounded-sm bg-red-500/10 flex items-center justify-center">
                    <ArrowDownRight className="w-5 h-5 text-red-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 border-slate-800" data-testid="metric-ganancia">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Ganancia Neta</p>
                    <p className={`text-2xl font-bold font-barlow ${resumen.ganancia_neta >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{formatoMXN(resumen.ganancia_neta)}</p>
                  </div>
                  <div className="w-10 h-10 rounded-sm bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 border-slate-800 cursor-pointer hover:border-amber-800 transition-colors" onClick={() => setTab('nomina')} data-testid="metric-nomina">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-2">Nomina</p>
                    <p className="text-2xl font-bold text-amber-500 font-barlow">{formatoMXN(resumen.total_nomina)}</p>
                    <p className="text-[10px] text-slate-600 mt-1">Clic para ver desglose</p>
                  </div>
                  <div className="w-10 h-10 rounded-sm bg-amber-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-2"><CardTitle className="font-barlow text-sm text-slate-400 uppercase tracking-wider">Ingresos por Metodo</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(resumen.ingresos_metodo || {}).length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Sin ingresos en este periodo</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(resumen.ingresos_metodo).map(([metodo, data]) => (
                      <div key={metodo} className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
                        <div>
                          <span className="text-sm text-slate-300 capitalize">{metodo}</span>
                          <span className="text-xs text-slate-600 ml-2">({data.count} pagos)</span>
                        </div>
                        <span className="text-sm font-bold text-green-400 font-mono">{formatoMXN(data.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="bg-slate-900/40 border-slate-800">
              <CardHeader className="pb-2"><CardTitle className="font-barlow text-sm text-slate-400 uppercase tracking-wider">Gastos por Categoria</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(resumen.gastos_categoria || {}).length === 0 ? (
                  <p className="text-sm text-slate-500 py-4 text-center">Sin gastos en este periodo</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(resumen.gastos_categoria).map(([cat, data]) => (
                      <div key={cat} className="flex items-center justify-between py-1.5 border-b border-slate-800/50">
                        <div>
                          <span className="text-sm text-slate-300 capitalize">{cat}</span>
                          <span className="text-xs text-slate-600 ml-2">({data.count})</span>
                        </div>
                        <span className="text-sm font-bold text-red-400 font-mono">{formatoMXN(data.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* TAB: Ingresos */}
      {tab === 'ingresos' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setTab('resumen')} className="text-slate-400 hover:text-slate-200" data-testid="volver-resumen-ing">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al Corte de Caja
          </Button>
          <div className="flex items-center justify-between">
            <h2 className="font-barlow text-lg font-bold text-green-400 uppercase tracking-wider">Desglose de Ingresos</h2>
            {resumen && <span className="text-sm text-slate-500">Total: <span className="text-green-400 font-bold font-mono">{formatoMXN(resumen.ingresos)}</span></span>}
          </div>
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Orden</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Cliente</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Metodo</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Referencia</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ingresos.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin ingresos en este periodo</TableCell></TableRow>
                  ) : ingresos.map(p => (
                    <TableRow key={p.id} className="border-slate-800/50 hover:bg-slate-800/40" data-testid={`ingreso-row-${p.id}`}>
                      <TableCell className="text-sm text-slate-400">{p.fecha_pago}</TableCell>
                      <TableCell><Badge variant="outline" className="text-amber-400 border-amber-800 text-xs font-mono">{p.numero_orden}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-300">{p.cliente_nombre}</TableCell>
                      <TableCell><Badge variant="outline" className="text-slate-400 border-slate-700 text-xs capitalize">{p.metodo_pago}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-500">{p.referencia || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-bold text-green-400 font-mono">{formatoMXN(p.monto)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Gastos */}
      {tab === 'gastos' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setTab('resumen')} className="text-slate-400 hover:text-slate-200" data-testid="volver-resumen-gas">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al Corte de Caja
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-barlow text-lg font-bold text-red-400 uppercase tracking-wider">Desglose de Gastos</h2>
              {resumen && <span className="text-xs text-slate-500">Total: <span className="text-red-400 font-bold font-mono">{formatoMXN(resumen.gastos)}</span></span>}
            </div>
            <Button onClick={() => { setGastoForm({ fecha: hoyLocal(), categoria: 'otros', descripcion: '', monto: '' }); setGastoDialog(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-gasto-btn">
              <Plus className="w-4 h-4 mr-2" /> Registrar Gasto
            </Button>
          </div>
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Categoria</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Descripcion</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Creado por</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Monto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gastos.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin gastos en este periodo</TableCell></TableRow>
                  ) : gastos.map(g => (
                    <TableRow key={g.id} className="border-slate-800/50 hover:bg-slate-800/40" data-testid={`gasto-row-${g.id}`}>
                      <TableCell className="text-sm text-slate-400">{g.fecha}</TableCell>
                      <TableCell><Badge variant="outline" className="text-slate-400 border-slate-700 text-xs capitalize">{g.categoria}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-300">{g.descripcion}</TableCell>
                      <TableCell className="text-sm text-slate-500">{g.creado_por || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-bold text-red-400 font-mono">{formatoMXN(g.monto)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => eliminarGasto(g.id)} className="text-red-400 hover:text-red-300" data-testid={`eliminar-gasto-${g.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* TAB: Nomina */}
      {tab === 'nomina' && (
        <div className="space-y-4">
          <Button variant="ghost" size="sm" onClick={() => setTab('resumen')} className="text-slate-400 hover:text-slate-200" data-testid="volver-resumen-nom">
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al Corte de Caja
          </Button>
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-barlow text-lg font-bold text-amber-500 uppercase tracking-wider">Desglose de Nomina</h2>
              {resumen && <span className="text-xs text-slate-500">Total: <span className="text-amber-500 font-bold font-mono">{formatoMXN(resumen.total_nomina)}</span></span>}
            </div>
            <Button onClick={() => { setNominaForm({ empleado_id: '', empleado_nombre: '', fecha: hoyLocal(), periodo: '', monto: '', notas: '' }); setNominaDialog(true); }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-sm" data-testid="nuevo-nomina-btn">
              <Plus className="w-4 h-4 mr-2" /> Registrar Pago
            </Button>
          </div>
          <Card className="bg-slate-900/40 border-slate-800">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Fecha</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Empleado</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Periodo</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500">Notas</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Monto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nomina.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Sin pagos de nomina en este periodo</TableCell></TableRow>
                  ) : nomina.map(n => (
                    <TableRow key={n.id} className="border-slate-800/50 hover:bg-slate-800/40" data-testid={`nomina-row-${n.id}`}>
                      <TableCell className="text-sm text-slate-400">{n.fecha}</TableCell>
                      <TableCell className="text-sm text-slate-300">{n.empleado_nombre}</TableCell>
                      <TableCell className="text-sm text-slate-400">{n.periodo}</TableCell>
                      <TableCell className="text-sm text-slate-500">{n.notas || '-'}</TableCell>
                      <TableCell className="text-right text-sm font-bold text-amber-500 font-mono">{formatoMXN(n.monto)}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => eliminarNomina(n.id)} className="text-red-400 hover:text-red-300" data-testid={`eliminar-nomina-${n.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog: Gasto */}
      <Dialog open={gastoDialog} onOpenChange={setGastoDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="gasto-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Registrar Gasto</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha</Label>
                <Input type="date" value={gastoForm.fecha} onChange={e => setGastoForm({...gastoForm, fecha: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="gasto-fecha-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Categoria</Label>
                <Select value={gastoForm.categoria} onValueChange={v => setGastoForm({...gastoForm, categoria: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="gasto-categoria-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    {CATEGORIAS.map(c => <SelectItem key={c.valor} value={c.valor} className="text-slate-200">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Descripcion *</Label>
              <Input value={gastoForm.descripcion} onChange={e => setGastoForm({...gastoForm, descripcion: e.target.value})}
                placeholder="Ej: Compra de aceite, Pago de renta..."
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="gasto-descripcion-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Monto (MXN) *</Label>
              <Input type="number" value={gastoForm.monto} onChange={e => setGastoForm({...gastoForm, monto: e.target.value})}
                placeholder="0.00"
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="gasto-monto-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGastoDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crearGasto} disabled={guardandoGasto} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-gasto-btn">
              {guardandoGasto ? 'Guardando...' : 'Registrar Gasto'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nomina */}
      <Dialog open={nominaDialog} onOpenChange={setNominaDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md" data-testid="nomina-dialog">
          <DialogHeader><DialogTitle className="font-barlow text-xl text-slate-100">Registrar Pago de Nomina</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Empleado *</Label>
              <Select value={nominaForm.empleado_id} onValueChange={v => {
                const emp = staff.find(s => s.id === v);
                setNominaForm({...nominaForm, empleado_id: v, empleado_nombre: emp?.nombre || ''});
              }}>
                <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-200" data-testid="nomina-empleado-select">
                  <SelectValue placeholder="Seleccionar empleado" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {staff.map(s => <SelectItem key={s.id} value={s.id} className="text-slate-200">{s.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Fecha</Label>
                <Input type="date" value={nominaForm.fecha} onChange={e => setNominaForm({...nominaForm, fecha: e.target.value})}
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="nomina-fecha-input" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-slate-400">Periodo</Label>
                <Input value={nominaForm.periodo} onChange={e => setNominaForm({...nominaForm, periodo: e.target.value})}
                  placeholder="Ej: Semana 11, Quincena Mar-1"
                  className="bg-slate-950 border-slate-700 text-slate-200" data-testid="nomina-periodo-input" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Monto (MXN) *</Label>
              <Input type="number" value={nominaForm.monto} onChange={e => setNominaForm({...nominaForm, monto: e.target.value})}
                placeholder="0.00"
                className="bg-slate-950 border-slate-700 text-slate-200 font-mono" data-testid="nomina-monto-input" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-slate-400">Notas</Label>
              <Input value={nominaForm.notas} onChange={e => setNominaForm({...nominaForm, notas: e.target.value})}
                className="bg-slate-950 border-slate-700 text-slate-200" data-testid="nomina-notas-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNominaDialog(false)} className="text-slate-400">Cancelar</Button>
            <Button onClick={crearNomina} disabled={guardandoNomina} className="bg-amber-500 hover:bg-amber-600 text-black font-bold" data-testid="guardar-nomina-btn">
              {guardandoNomina ? 'Guardando...' : 'Registrar Pago'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
